// src/command/useCommandEngine.ts

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyGlobalSearchQuery, SearchResult } from '../api/searchApi';
import { useCentralContext, hasCapability } from '../services/centralContext';
import {
  allCommands,
  searchCommands,
  filterCommandsByCapability,
  categoryLabels,
} from './commandRegistry';
import { Command, CommandCategory } from './types';

// ============================================================
// Types
// ============================================================

export interface CommandEngineItem {
  type: 'command' | 'result';
  id: string;
  label: string;
  subtitle?: string;
  category?: CommandCategory;
  resultType?: string;
  icon?: string;
  route?: string;
  action?: (navigate: (path: string) => void) => void;
}

export interface CommandEngineState {
  isOpen: boolean;
  query: string;
  selectedIndex: number;
  isLoading: boolean;
}

export interface CommandEngineResult {
  commands: CommandEngineItem[];
  searchResults: CommandEngineItem[];
  allItems: CommandEngineItem[];
  groupedCommands: Record<string, CommandEngineItem[]>;
}

// ============================================================
// Debounce Helper
// ============================================================

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ============================================================
// Command Engine Hook
// ============================================================

export function useCommandEngine() {
  const navigate = useNavigate();
  const centralContext = useCentralContext();

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Search API
  const [triggerSearch, { data: searchData, isLoading: searchLoading, isFetching }] =
    useLazyGlobalSearchQuery();

  // Debounce search query
  const debouncedQuery = useDebounce(query, 300);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim().length >= 2 && isOpen) {
      triggerSearch({ query: debouncedQuery, limit: 10 });
    }
  }, [debouncedQuery, isOpen, triggerSearch]);

  // Filter commands by Central capability
  const capabilityChecker = useCallback(
    (domain: string, action: string) => {
      if (!centralContext) return false;
      return hasCapability(centralContext, domain, action);
    },
    [centralContext]
  );

  const availableCommands = useMemo(() => {
    return filterCommandsByCapability(allCommands, capabilityChecker);
  }, [capabilityChecker]);

  // Search/filter commands
  const filteredCommands = useMemo(() => {
    if (!query.trim()) {
      return availableCommands;
    }
    return searchCommands(availableCommands, query);
  }, [availableCommands, query]);

  // Convert commands to engine items
  const commandItems: CommandEngineItem[] = useMemo(() => {
    return filteredCommands.map((cmd) => ({
      type: 'command' as const,
      id: cmd.id,
      label: cmd.label,
      subtitle: cmd.description,
      category: cmd.category,
      icon: cmd.icon,
      action: cmd.action,
    }));
  }, [filteredCommands]);

  // Convert search results to engine items
  const searchResultItems: CommandEngineItem[] = useMemo(() => {
    if (!searchData?.results) return [];
    return searchData.results.map((result) => ({
      type: 'result' as const,
      id: `result-${result.id}`,
      label: result.title,
      subtitle: result.subtitle,
      resultType: result.type,
      route: result.route,
      icon: getIconForResultType(result.type),
    }));
  }, [searchData]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandEngineItem[]> = {};
    for (const item of commandItems) {
      const category = item.category || 'navigation';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    }
    return groups;
  }, [commandItems]);

  // All items for navigation
  const allItems = useMemo(() => {
    return [...commandItems, ...searchResultItems];
  }, [commandItems, searchResultItems]);

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(0);
  }, [allItems.length]);

  // Open/close handlers
  const open = useCallback(() => {
    setIsOpen(true);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setSelectedIndex(0);
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, open, close]);

  // Navigation handlers
  const selectNext = useCallback(() => {
    setSelectedIndex((prev) => (prev + 1) % allItems.length);
  }, [allItems.length]);

  const selectPrevious = useCallback(() => {
    setSelectedIndex((prev) => (prev - 1 + allItems.length) % allItems.length);
  }, [allItems.length]);

  const selectIndex = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  // Execute selected item
  const executeSelected = useCallback(() => {
    const item = allItems[selectedIndex];
    if (!item) return;

    if (item.type === 'command' && item.action) {
      item.action(navigate);
    } else if (item.type === 'result' && item.route) {
      navigate(item.route);
    }

    close();
  }, [allItems, selectedIndex, navigate, close]);

  // Execute specific item
  const executeItem = useCallback(
    (item: CommandEngineItem) => {
      if (item.type === 'command' && item.action) {
        item.action(navigate);
      } else if (item.type === 'result' && item.route) {
        navigate(item.route);
      }

      close();
    },
    [navigate, close]
  );

  // Keyboard handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          selectNext();
          break;
        case 'ArrowUp':
          e.preventDefault();
          selectPrevious();
          break;
        case 'Enter':
          e.preventDefault();
          executeSelected();
          break;
        case 'Escape':
          e.preventDefault();
          close();
          break;
      }
    },
    [isOpen, selectNext, selectPrevious, executeSelected, close]
  );

  // Global keyboard shortcut
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Cmd+K or Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [toggle]);

  // Attach keyboard handler when open
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  return {
    // State
    isOpen,
    query,
    selectedIndex,
    isLoading: searchLoading || isFetching,

    // Results
    commands: commandItems,
    searchResults: searchResultItems,
    allItems,
    groupedCommands,
    categoryLabels,

    // Actions
    open,
    close,
    toggle,
    setQuery,
    selectNext,
    selectPrevious,
    selectIndex,
    executeSelected,
    executeItem,
  };
}

// ============================================================
// Helper Functions
// ============================================================

function getIconForResultType(type: string): string {
  const iconMap: Record<string, string> = {
    contact: 'user',
    task: 'check-square',
    opportunity: 'trending-up',
    pipeline: 'git-branch',
    event: 'calendar',
    file: 'file',
    form: 'clipboard',
    project: 'folder',
    message: 'mail',
    channel: 'radio',
    team_member: 'users',
    workspace: 'briefcase',
  };
  return iconMap[type] || 'circle';
}

// ============================================================
// Export for mobile reuse
// ============================================================

export { allCommands, categoryLabels, searchCommands, filterCommandsByCapability };
export type { Command, CommandCategory };
